import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Single source of truth for image coordinate transforms.
 *
 * Three coordinate spaces we care about:
 *
 *   - imageSpace  — natural px (e.g. 3024×4032). The backend
 *                   speaks this: all bbox + mask PNG coordinates
 *                   are in this system.
 *   - domSpace    — CSS px where the user actually points. Varies
 *                   with window size, `object-fit: contain` letter-
 *                   boxing, and CSS transforms on ancestors.
 *   - normalized  — 0..1 across the rendered image. Useful if we
 *                   ever re-render at a different size (drag vs
 *                   persisted state).
 *
 * Every layer in the segmentation stage — bboxes, masks, drag
 * handlers, click tests — MUST consume only this hook. Anything
 * else (hand-rolling `getBoundingClientRect` + dividing by
 * naturalWidth) drifts the moment the window resizes or the image
 * letterboxes.
 *
 * Web-only for the current build: web DOM gives us
 * ResizeObserver + `<img>.naturalWidth`/`.naturalHeight`. Native
 * would need a separate `onLayout` + `Image.getSize` path — out of
 * scope until we ship native streaming.
 */

export interface Bbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface ImageTransform {
  /** Natural image dimensions in px. `null` until `<img>` loads. */
  imageSize: { width: number; height: number } | null;
  /** The rect INSIDE the container where the image actually renders
   *  after `object-fit: contain` letterboxing. Local to the
   *  container — x/y are offsets from the container's top-left. */
  renderRect: Bbox | null;
  /** Convert a DOM-space point (relative to container) to image px. */
  toImage: (p: Point) => Point;
  /** Convert an image-px point to DOM space (relative to container). */
  toDom: (p: Point) => Point;
  /** Convert an image-px bbox to DOM-space for <rect> positioning. */
  toDomBbox: (b: Bbox) => Bbox;
  /** Convert a DOM bbox to image-px for API writes. */
  toImageBbox: (b: Bbox) => Bbox;
  /** True once the image has loaded AND we've measured the render
   *  rect at least once. Overlay layers should skip rendering until
   *  this is true — otherwise bboxes briefly land at (0,0). */
  ready: boolean;
}

const IDENTITY: ImageTransform = {
  imageSize: null,
  renderRect: null,
  toImage: (p) => p,
  toDom: (p) => p,
  toDomBbox: (b) => b,
  toImageBbox: (b) => b,
  ready: false,
};

/**
 * Hook produces transform helpers + listens for size changes.
 *
 * Usage:
 *
 *   const imgRef = useRef<HTMLImageElement>(null);
 *   const containerRef = useRef<HTMLDivElement>(null);
 *   const tx = useImageTransform(imgRef, containerRef);
 *   // …
 *   <div ref={containerRef} style={{position:'relative'}}>
 *     <img ref={imgRef} src={url} onLoad={tx.onImageLoad} … />
 *     {tx.ready && <Bbox rect={tx.toDomBbox(item.bbox)} … />}
 *   </div>
 *
 * NOTE: the caller wires the `onLoad` handler explicitly — we
 * can't use `imgRef.current?.addEventListener('load')` from the
 * hook because React strict-mode double-mounts would double-bind.
 */
export function useImageTransform(
  imgRef: React.RefObject<HTMLImageElement | null>,
  containerRef: React.RefObject<HTMLElement | null>,
): ImageTransform & { onImageLoad: () => void } {
  const [imageSize, setImageSize] = useState<
    { width: number; height: number } | null
  >(null);
  const [containerSize, setContainerSize] = useState<
    { width: number; height: number } | null
  >(null);

  // Measure the container box on mount + whenever it resizes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setContainerSize({ width: cr.width, height: cr.height });
      }
    });
    ro.observe(el);
    // Seed once before the observer fires.
    const rect = el.getBoundingClientRect();
    setContainerSize({ width: rect.width, height: rect.height });
    return () => ro.disconnect();
  }, [containerRef]);

  // Seeded by the caller via <img onLoad={tx.onImageLoad} />. We
  // deliberately don't attach a listener ourselves — strict-mode
  // remount semantics would double-fire.
  const onImageLoad = useCallback(() => {
    const el = imgRef.current;
    if (!el) return;
    const w = el.naturalWidth;
    const h = el.naturalHeight;
    if (w > 0 && h > 0) setImageSize({ width: w, height: h });
  }, [imgRef]);

  // Compute the letterbox offset. `object-fit: contain` centers
  // the image inside the container — we need to account for the
  // dead band on the top/bottom OR left/right depending on which
  // aspect ratio is wider.
  const computed = computeTransform(imageSize, containerSize);
  const ready = computed !== null;
  const txRef = useRef(computed ?? IDENTITY);
  if (computed) txRef.current = computed;

  const toImage = useCallback(
    (p: Point) => txRef.current.toImage(p),
    [],
  );
  const toDom = useCallback((p: Point) => txRef.current.toDom(p), []);
  const toDomBbox = useCallback(
    (b: Bbox) => txRef.current.toDomBbox(b),
    [],
  );
  const toImageBbox = useCallback(
    (b: Bbox) => txRef.current.toImageBbox(b),
    [],
  );

  return {
    imageSize,
    renderRect: txRef.current.renderRect,
    toImage,
    toDom,
    toDomBbox,
    toImageBbox,
    ready,
    onImageLoad,
  };
}

/**
 * Compute transforms + render rect for a given (imageSize,
 * containerSize) pair. Exported for unit-tests — the hook wraps
 * this in React state. Returns `null` until both dimensions are
 * known.
 */
export function computeTransform(
  imageSize: { width: number; height: number } | null,
  containerSize: { width: number; height: number } | null,
): ImageTransform | null {
  if (!imageSize || !containerSize) return null;
  if (imageSize.width === 0 || imageSize.height === 0) return null;
  if (containerSize.width === 0 || containerSize.height === 0) return null;

  const imgAspect = imageSize.width / imageSize.height;
  const cntAspect = containerSize.width / containerSize.height;

  let renderW: number;
  let renderH: number;
  let offsetX: number;
  let offsetY: number;

  if (imgAspect > cntAspect) {
    // Image is relatively wider → letterbox top/bottom.
    renderW = containerSize.width;
    renderH = containerSize.width / imgAspect;
    offsetX = 0;
    offsetY = (containerSize.height - renderH) / 2;
  } else {
    // Image is relatively taller → letterbox left/right.
    renderH = containerSize.height;
    renderW = containerSize.height * imgAspect;
    offsetX = (containerSize.width - renderW) / 2;
    offsetY = 0;
  }

  const scale = renderW / imageSize.width; // === renderH / imageSize.height

  const toImage = (p: Point): Point => ({
    x: (p.x - offsetX) / scale,
    y: (p.y - offsetY) / scale,
  });

  const toDom = (p: Point): Point => ({
    x: p.x * scale + offsetX,
    y: p.y * scale + offsetY,
  });

  const toDomBbox = (b: Bbox): Bbox => ({
    x: b.x * scale + offsetX,
    y: b.y * scale + offsetY,
    w: b.w * scale,
    h: b.h * scale,
  });

  const toImageBbox = (b: Bbox): Bbox => ({
    x: (b.x - offsetX) / scale,
    y: (b.y - offsetY) / scale,
    w: b.w / scale,
    h: b.h / scale,
  });

  return {
    imageSize,
    renderRect: { x: offsetX, y: offsetY, w: renderW, h: renderH },
    toImage,
    toDom,
    toDomBbox,
    toImageBbox,
    ready: true,
  };
}
